import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Skeleton,
  useTheme,
  alpha,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Stack,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  AttachMoney,
  Group,
  HelpOutline,
  LocationCity,
  Percent,
  ReceiptLong,
  TrendingDown,
  Warning,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Legend,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

import invoiceService from '../../../services/invoiceService';
import { clientService } from '../../../services/clientService';
import { dashboardService } from '../../../services/dashboardService';
import ClientesTable from '../../../components/client/ClientesTable';
import { Client, Invoice, CobrancaFinanceiro } from '../../../types';
import { formatarValor } from '../../../utils/formatToView';
import CriarCobrancaModal from './CriarCobrancaModal';

/**
 * Calcula a diferença de dias entre a data de vencimento e hoje.
 */
const getDaysOverdue = (dueDate: string) => {
  const today = new Date();
  const due = new Date(dueDate);
  // Garante que comparamos apenas datas, ignorando a hora
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  const diffTime = today.getTime() - due.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
};

// ─── Configuração de Status ───────────────────────────────────────────────────
const CLASSIFICATION_CONFIG = {
  leve: {
    icon: "🟢",
    label: "Atraso leve (1–10 dias)",
    color: "#166534",
    bg: "#dcfce7"
  },
  atencao: {
    icon: "⚠️",
    label: "Atenção (11–30 dias)",
    color: "#854d0e",
    bg: "#fef9c3"
  },
  risco: {
    icon: "❗",
    label: "Em risco (31–60 dias)",
    color: "#9a3412",
    bg: "#ffedd5"
  },
  critico: {
    icon: "🚨",
    label: "Crítico (61–90 dias)",
    color: "#991b1b",
    bg: "#fecaca"
  },
  extremo: {
    icon: "💀",
    label: "Extremamente crítico (> 90 dias)",
    color: "#111827",
    bg: "#e5e7eb"
  }
};

interface FinanceiroDashboardTabProps {
  escritorioId?: number | null;
  onCobrancaCreated?: (cobranca: CobrancaFinanceiro) => void;
  onManageCobranca?: (id: number) => void;
}



const FinanceiroDashboardTab: React.FC<FinanceiroDashboardTabProps> = ({ escritorioId, onCobrancaCreated, onManageCobranca }) => {


  const theme = useTheme();
  
  // Estados de Filtro e UI
  const [selectedClassification, setSelectedClassification] = useState<keyof typeof CLASSIFICATION_CONFIG | null>(null);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<string | null>(null);
  const [hideInactive, setHideInactive] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [orderBy, setOrderBy] = useState<'id' | 'nome' | 'createdAt'>('id');
  const [orderDir, setOrderDir] = useState<'asc' | 'desc'>('asc');
  const tableRef = useRef<HTMLDivElement>(null);

  // Estados para o Modal de Cobrança
  const [modalOpen, setModalOpen] = useState(false);
  const [currentClient, setCurrentClient] = useState<Client | null>(null);

  // Busca todas as faturas vencidas
  const { data: invoicesResp, isLoading: loadingInvoices, refetch: refetchInvoices } = useQuery({
    queryKey: ['invoices-overdue-financeiro-dashboard', escritorioId],
    queryFn: () => invoiceService.getInvoices({
      status: 'vencido',
      limit: 5000,
      ...(escritorioId ? { escritorio_id: escritorioId } : {}),
    } as any),
    staleTime: 60_000,
  });

  // Busca o total de clientes para calcular a taxa de inadimplência
  const { data: clientsCountResp, isLoading: loadingCount } = useQuery({
    queryKey: ['clients-total-count-financeiro-dashboard', escritorioId],
    queryFn: () => clientService.getAll({
      limit: 1,
      ...(escritorioId ? { escritorio_id: escritorioId } : {}),
    } as any),
    staleTime: 300_000,
  });

  const { data: dashboardStats, isLoading: loadingTicketMedio } = useQuery({
    queryKey: ['dashboard-stats', 'financeiro-dashboard-tab'],
    queryFn: () => dashboardService.getStats(),
    staleTime: 60_000,
  });

  const ticketMedioFatura = dashboardStats?.averageTicketPerInvoice ?? 0;

  const isLoading = loadingInvoices || loadingCount;
  const invoices: Invoice[] = (invoicesResp as any)?.data ?? [];
  const totalClientsCount = (clientsCountResp as any)?.total || 0;

  // Lógica de agrupamento e processamento dos dados
  const { clientsData, classifications, neighborhoodData, totalDelinquency, clientsByNeighborhood, delinquencyRate, filteredInvoicesQty } = useMemo(() => {
    const clientMap = new Map<number, { client: Client, invoices: Invoice[], oldestDueDate: string, totalValue: number }>();
    let total = 0;
    let filteredInvoicesQty = 0;

    invoices.forEach(inv => {
      if (!inv.cliente) return;
      if (hideInactive && inv.cliente.status === 'inativo') return;

      const val = (Number(inv.valor) + (Number(inv.acrescimo) || 0) - (Number(inv.desconto) || 0));
      total += val;
      filteredInvoicesQty++;

      const clientId = inv.cliente.id;
      if (!clientMap.has(clientId)) {
        clientMap.set(clientId, {
          client: inv.cliente,
          invoices: [inv],
          oldestDueDate: inv.data_vencimento,
          totalValue: val
        });
      } else {
        const entry = clientMap.get(clientId)!;
        entry.invoices.push(inv);
        entry.totalValue += val;
        if (new Date(inv.data_vencimento) < new Date(entry.oldestDueDate)) {
          entry.oldestDueDate = inv.data_vencimento;
        }
      }
    });

    const neighborhoodMap = new Map<string, number>();
    const neighborhoodClientsMap = new Map<string, Client[]>();

    const classCount: Record<keyof typeof CLASSIFICATION_CONFIG, { label: string, count: number, value: number, clients: Client[] }> = {
      leve: { label: CLASSIFICATION_CONFIG.leve.label, count: 0, value: 0, clients: [] as Client[] },
      atencao: { label: CLASSIFICATION_CONFIG.atencao.label, count: 0, value: 0, clients: [] as Client[] },
      risco: { label: CLASSIFICATION_CONFIG.risco.label, count: 0, value: 0, clients: [] as Client[] },
      critico: { label: CLASSIFICATION_CONFIG.critico.label, count: 0, value: 0, clients: [] as Client[] },
      extremo: { label: CLASSIFICATION_CONFIG.extremo.label, count: 0, value: 0, clients: [] as Client[] },
    };

    clientMap.forEach(data => {
      const days = getDaysOverdue(data.oldestDueDate);
      let cat: keyof typeof CLASSIFICATION_CONFIG;

      if (days <= 10) cat = 'leve';
      else if (days <= 30) cat = 'atencao';
      else if (days <= 60) cat = 'risco';
      else if (days <= 90) cat = 'critico';
      else cat = 'extremo';

      const enrichedClient = {
        ...data.client,
        _qtyVencidas: data.invoices.length,
        _oldestVencimento: data.oldestDueDate,
        _totalDevido: data.totalValue
      };

      classCount[cat].count++;
      classCount[cat].value += data.totalValue;
      classCount[cat].clients.push(enrichedClient as any);

      const neighborhood = data.client.bairro || 'Não Informado';
      neighborhoodMap.set(neighborhood, (neighborhoodMap.get(neighborhood) || 0) + data.totalValue);

      if (!neighborhoodClientsMap.has(neighborhood)) {
        neighborhoodClientsMap.set(neighborhood, [enrichedClient as any]);
      } else {
        neighborhoodClientsMap.get(neighborhood)!.push(enrichedClient as any);
      }
    });

    const neighborhoodChart = Array.from(neighborhoodMap.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);

    const rate = totalClientsCount > 0 ? (clientMap.size / totalClientsCount) * 100 : 0;

    return {
      clientsData: clientMap,
      classifications: Object.entries(classCount).map(([key, val]) => ({ key: key as keyof typeof CLASSIFICATION_CONFIG, ...val })),
      neighborhoodData: neighborhoodChart,
      totalDelinquency: total,
      clientsByNeighborhood: neighborhoodClientsMap,
      delinquencyRate: rate,
      filteredInvoicesQty
    };
  }, [invoices, totalClientsCount, hideInactive]);

  useEffect(() => {
    if (selectedClassification || selectedNeighborhood) {
      setPage(0);
      setTimeout(() => {
        tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [selectedClassification, selectedNeighborhood]);

  const selectedClients = useMemo(() => {
    if (selectedNeighborhood) {
      return clientsByNeighborhood.get(selectedNeighborhood) || [];
    }
    if (selectedClassification) {
      return classifications.find(c => c.key === selectedClassification)?.clients || [];
    }
    return [];
  }, [selectedClassification, selectedNeighborhood, classifications, clientsByNeighborhood]);

  const tableInfo = useMemo(() => {
    if (selectedNeighborhood) {
      return {
        title: `Detalhamento: Bairro ${selectedNeighborhood}`,
        color: theme.palette.primary.main,
        textColor: '#FFFFFF'
      };
    }
    if (selectedClassification) {
      const config = CLASSIFICATION_CONFIG[selectedClassification];
      return {
        title: `Detalhamento: ${config.label}`,
        color: config.bg,
        textColor: config.color
      };
    }
    return {
      title: "Detalhamento de Clientes (Selecione uma faixa ou bairro)",
      color: theme.palette.grey[400],
      textColor: theme.palette.text.primary
    };
  }, [selectedClassification, selectedNeighborhood, theme]);

  const handleNeighborhoodClick = (data: any) => {
    if (data && data.name) {
      setSelectedClassification(null);
      setSelectedNeighborhood(prev => prev === data.name ? null : data.name);
    }
  };

  const handleClassificationClick = (key: keyof typeof CLASSIFICATION_CONFIG) => {
    setSelectedNeighborhood(null);
    setSelectedClassification(prev => prev === key ? null : key);
  };

  const handleSort = (col: 'id' | 'nome' | 'createdAt') => {
    const isAsc = orderBy === col && orderDir === 'asc';
    setOrderDir(isAsc ? 'desc' : 'asc');
    setOrderBy(col);
  };

  const handleRowClick = (client: Client) => {
    if (client.cobranca_id) {
      if (onManageCobranca) onManageCobranca(Number(client.cobranca_id));
      return;
    }
    setCurrentClient(client);
    setModalOpen(true);
  };


  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4, 5].map(i => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={6}>
            <Skeleton variant="rectangular" height={360} sx={{ borderRadius: 2 }} />
          </Grid>
          <Grid item xs={12} lg={6}>
            <Skeleton variant="rectangular" height={360} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      {/* Cards de Resumo */}
      <Grid container spacing={1.5} sx={{ mb: 1 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            elevation={1}
            sx={{
              p: 1,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              minHeight: 100,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 700, textTransform: 'uppercase' }}>
                Dívida Total
              </Typography>
              <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: -0.5 }}>
                {formatarValor(totalDelinquency)}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                <TrendingDown sx={{ fontSize: 14, opacity: 0.8 }} />
                <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600 }}>
                  {filteredInvoicesQty} faturas vencidas
                </Typography>
              </Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={hideInactive}
                    onChange={(e) => setHideInactive(e.target.checked)}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-checked': { color: '#fff' },
                      p: 0.5
                    }}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600, fontSize: '0.7rem' }}>
                    Ocultar Inativos
                  </Typography>
                }
                sx={{ ml: -0.5, mt: 0.5, mr: 0 }}
              />
            </Box>
            <Warning sx={{ position: 'absolute', right: -10, bottom: -10, fontSize: 100, opacity: 0.1, transform: 'rotate(-15deg)' }} />
          </Paper>
        </Grid>

        <Grid item xs={6} sm={6} md={2.4}>
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main', width: 44, height: 44 }}>
                <Group sx={{ fontSize: 22 }} />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', fontSize: '0.65rem' }}>
                  Devedores
                </Typography>
                <Typography variant="h5" fontWeight={800} color="text.primary">
                  {clientsData.size}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', display: 'block' }}>
                  de {totalClientsCount} totais
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={6} sm={6} md={2.4}>
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.08), color: 'warning.dark', width: 44, height: 44 }}>
                <Percent sx={{ fontSize: 22 }} />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', fontSize: '0.65rem' }}>
                  Taxa Inadimplência
                </Typography>
                <Typography variant="h5" fontWeight={800} color="text.primary">
                  {delinquencyRate.toFixed(1)}%
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', display: 'block' }}>
                  índice de risco
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.08), color: 'success.main', width: 44, height: 44 }}>
                <AttachMoney sx={{ fontSize: 22 }} />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', fontSize: '0.65rem' }}>
                  Média / Devedor
                </Typography>
                <Typography variant="h5" fontWeight={800} color="text.primary" sx={{ fontSize: '1.2rem' }}>
                  {formatarValor(clientsData.size > 0 ? totalDelinquency / clientsData.size : 0)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Tooltip title="Média do valor líquido (valor + acréscimos − descontos) das faturas pagas no mês atual.">
            <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.08), color: 'info.main', width: 44, height: 44 }}>
                  <ReceiptLong sx={{ fontSize: 22 }} />
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', fontSize: '0.65rem' }}>
                    Ticket médio (fatura)
                  </Typography>
                  {loadingTicketMedio ? (
                    <Skeleton variant="text" width={120} height={32} sx={{ mt: 0.5 }} />
                  ) : (
                    <Typography variant="h5" fontWeight={800} color="text.primary" sx={{ fontSize: '1.2rem' }}>
                      {formatarValor(ticketMedioFatura)}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', display: 'block' }}>
                    faturas pagas no mês
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Tooltip>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Gráfico Pizza por Bairro */}
        <Grid item xs={12} lg={6}>
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: 360, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main', width: 28, height: 28 }}>
                  <LocationCity sx={{ fontSize: 16 }} />
                </Avatar>
                <Typography variant="subtitle2" fontWeight={700}>Bairros mais Inadimplentes</Typography>
              </Box>
              <Tooltip title="Soma dos valores devidos pelos clientes de cada bairro">
                <HelpOutline sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
              </Tooltip>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={neighborhoodData}
                    cx="50%" cy="50%"
                    innerRadius={60} outerRadius={100}
                    paddingAngle={3}
                    dataKey="value" nameKey="name"
                    onClick={handleNeighborhoodClick}
                    style={{ cursor: 'pointer' }}
                  >
                    {neighborhoodData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={index === 0 ? theme.palette.error.main : theme.palette.primary.main}
                        fillOpacity={selectedNeighborhood === entry.name ? 1 : 1 - (index * 0.12)}
                        stroke={selectedNeighborhood === entry.name ? theme.palette.common.white : 'none'}
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <ReTooltip formatter={(val: number) => [formatarValor(val), 'Valor Devido']} contentStyle={{ borderRadius: 8, border: 'none', boxShadow: theme.shadows[3] }} />
                  <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" wrapperStyle={{ fontSize: '0.75rem', fontWeight: 600, paddingLeft: '10px' }} onClick={(e) => handleNeighborhoodClick({ name: e.value })} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Faixas de Atraso */}
        <Grid item xs={12} lg={6}>
          <Paper elevation={1} sx={{ p: 0, borderRadius: 2, height: 360, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.08), color: 'error.main', width: 28, height: 28 }}>
                  <Warning sx={{ fontSize: 16 }} />
                </Avatar>
                <Typography variant="subtitle2" fontWeight={700}>Faixas de Atraso</Typography>
              </Box>
            </Box>
            <Divider />
            <TableContainer sx={{ flexGrow: 1 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '0.75rem' }}>Classificação</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Clientes</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Valor Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {classifications.map((row) => {
                    const config = CLASSIFICATION_CONFIG[row.key];
                    const isSelected = selectedClassification === row.key;
                    return (
                      <TableRow
                        key={row.key} hover onClick={() => handleClassificationClick(row.key)}
                        sx={{
                          cursor: 'pointer', transition: 'all 0.15s', bgcolor: config.bg,
                          outline: isSelected ? `2px solid ${config.color}` : 'none',
                          zIndex: isSelected ? 1 : 0,
                          '&:hover': { bgcolor: alpha(config.bg, 0.8) }
                        }}
                      >
                        <TableCell sx={{ py: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography sx={{ fontSize: '1.1rem' }}>{config.icon}</Typography>
                            <Typography variant="body2" fontWeight={800} sx={{ fontSize: '0.75rem', color: config.color, filter: 'brightness(0.7)' }}>
                              {config.label}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          <Box sx={{ display: 'inline-block', px: 1, py: 0.2, borderRadius: 1.5, bgcolor: alpha(config.color, 0.15), color: config.color, fontWeight: 800, fontSize: '0.7rem', border: `1px solid ${alpha(config.color, 0.3)}` }}>
                            {row.count}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography variant="body2" fontWeight={900} sx={{ fontSize: '0.85rem', color: config.color }}>
                            {formatarValor(row.value)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabela de Detalhamento */}
      <Box ref={tableRef} sx={{ mt: 2 }}>
        <ClientesTable
          clientes={selectedClients}
          titulo={tableInfo.title}
          subtitulo={selectedClients.length > 0 ? `${selectedClients.length} clientes encontrados` : "Selecione um dado acima para visualizar a lista"}
          dismissible={false}
          open={true}
          headerColor={tableInfo.color}
          headerTextColor={tableInfo.textColor}
          showDelinquencyInfo={true}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          orderBy={orderBy}
          order={orderDir}
          onSort={handleSort}
          onRowClick={handleRowClick}
        />
      </Box>

      {/* Modal de Criação de Cobrança */}
      <CriarCobrancaModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        client={currentClient}
        overdueInvoices={invoices}
        onSuccess={(c) => {
          refetchInvoices();
          if (onCobrancaCreated) onCobrancaCreated(c);
        }}
      />

    </Box>
  );
};

export default FinanceiroDashboardTab;
